/* eslint-disable complexity */
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import queryString, { ParsedQuery } from 'query-string';
import { useAsync, useAsyncFn, useMount, useUpdateEffect } from 'react-use-v13';
import SAlert from 'react-s-alert';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  Radio,
  RadioGroup,
  TextButton,
  TextInput,
  NumericInput,
} from 'design-system';
import { H3 } from 'design-system/Typography';
// TODO: Migrate module so that you don’t import from the corp project in a global module
/* eslint-disable restrict-imports/restrict-imports */
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import { parseObjectIds } from 'corp/lib/parseInputText';
import SelectProject from 'corp/components/UI/SelectProject';
import SpeedAuditDialog from './SpeedAuditDialog';
import TextBasedSpeedAuditDialog from './TextBasedSpeedAuditDialog';
import { AuditBatch, SpeedAuditLookup, SpeedAuditResult, SubtaskAssignment } from './types';
import { ReviewLevel, SpeedAuditVersion } from '@scale/shared/defaults';
import { getInitialResult } from './utils';
import { IInstructionItemJson } from '@scale/shared/types/instructionitem';
import Spinner from 'design-system/Spinner';
import {
  AuditType,
  CreateAuditBatchBody,
  CreateAuditBatchResponse,
  GetAuditBatchResponse,
  IThresholdValues,
  AuditViewType,
} from '@scale/shared/types/corp/attemptAudit';
import { ISpotterAttempts } from '@scale/shared/types/corp/quality';
import SpotterAuditModal from './SpotterAuditModal';
import useMap from 'react-use-v13/lib/useMap';
import logger from '@scale/frontend-lib/logging';
import Bluebird from 'bluebird';
import { assertDefined, PartialRecord } from '@scale/shared/types/utils';
import IsomorphicLink from 'corp/components/UI/IsomorphicLink';
import { logCorpEvent } from '@scale/frontend-lib/analytics/log-corp-event';
import { Event } from '@scale/shared/types/events';
import { SpeedAuditCreated } from '@scale/shared/types/events/SpeedAudit';
import { useHistory } from 'react-router-dom';
import { QAAssignmentParams } from '@scale/shared/types/qaOperation/qaOperation';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';
import { SpeedAuditOperation } from '@scale/shared/types/qaOperation/operations/speedAuditOperation';
import { IUserJson } from '@scale/shared/types/user';
import { QAOperationAssignmentForm } from 'react-scale/components/remoAdmin/qaOperation/QAOperationAssignmentForm';
import { redirectToOperationsPage } from 'react-scale/components/remoAdmin/qaOperation/redirectToOperationsPage';
import { ThresholdFields } from './Gatekeeper/QualityLabModal';
import { IIMetricsData } from './Gatekeeper/types';
import { validateQAOperationParams } from 'react-scale/components/remoAdmin/qaOperation/tables/helpers';
import { QAOperationContextProvider } from 'react-scale/components/remoAdmin/qaOperation/qaOperationContext';
import {
  QMNonAuditedListSubtasksLimit,
  QMNonAuditedListSubtasksRequest,
  QMNonAuditedListSubtasksResponse,
} from '@scale/shared/qm/qmRequestTypes';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import WarningTasksDialog from './WarningTasksDialog';
import { TaskType } from '@scale/shared-types';
import { Text, TextArea } from '@scale/scaleui-radix';

interface SpeedAuditProps {
  showProjectChooser: boolean;
  url?: { query?: ParsedQuery };
  version?: SpeedAuditVersion;
  setVersion?: (newVersion: SpeedAuditVersion) => void;
  forceRedirectAuditId?: string | undefined;
  setForceRedirectAuditId?: (newValue: string) => void;
  auditBatchId?: string;
  hideVersionChoice?: boolean;
  createQAOperationDialogMode?: boolean;
  qaOperationName?: string;
  hideAdvancedFilters?: boolean;
  onGetOrCreateBatchOverride?: (batchId: string) => void;
  user: IUserJson;
  isPodHealth?: boolean;
}

interface ISelectedProject {
  label: string;
  value: string;
  type?: string;
}

const reviewLevelOptions: number[] = [
  ReviewLevel.Normal.valueOf(),
  ReviewLevel.Level1.valueOf(),
  ReviewLevel.Corp.valueOf(),
  ReviewLevel.Deliverable.valueOf(),
];
const HIGH_REVIEW_LEVELS = [ReviewLevel.Corp, ReviewLevel.Deliverable];

function isProjectChatOrChat2(project?: Record<string, any>): boolean {
  if (!project) {
    return false;
  }

  return project?.type === TaskType.Chat || project?.type === TaskType.ChatV2;
}

const SpeedAuditThresholdFields: React.FC<{
  project: { label: string; value: string };
  thresholdValues: IThresholdValues;
  setThresholdValues: (v: IThresholdValues) => void;
  setThresholdTasks: (v: string) => void;
  reviewLevel: ReviewLevel;
}> = props => {
  const { project, thresholdValues, setThresholdValues, setThresholdTasks, reviewLevel } = props;

  const [taskCounts, fetchTaskCounts] = useAsyncFn(async () => {
    assertDefined(project);
    return apiFetch<PartialRecord<ReviewLevel, number>>(
      null,
      `corp-api/quality/task_counts/${project.value}`,
      { method: 'GET' },
    );
  }, [project]);

  const [projectInstrItems, fetchProjectInstrItems] = useAsyncFn(async () => {
    assertDefined(project);
    const { benchmarkInstructionItems } = await apiFetch<{
      benchmarkInstructionItems: IInstructionItemJson[];
    }>(null, `corp-api/quality/${project.value}/project_iis`);
    return benchmarkInstructionItems;
  }, [project]);

  const [projectBatches, fetchProjectBatches] = useAsyncFn(async () => {
    assertDefined(project);
    const { projectBatches } = await apiFetch<{ projectBatches: { name: string; _id: string }[] }>(
      null,
      `corp-api/quality/${project.value}/batches`,
    );
    return projectBatches;
  }, [project]);

  const [metricsData, fetchMetricsData] = useAsyncFn(async () => {
    const params = {
      windowStartingAt: new Date(
        new Date().getTime() - thresholdValues.lookbackDays * 1000 * 60 * 60 * 24,
      ),
    };
    assertDefined(project);
    return apiFetch<IIMetricsData>(
      null,
      `corp-api/quality/${project.value}/ii_metrics?${queryString.stringify(params)}`,
      {
        method: 'GET',
        maxRetries: 1,
      },
    );
  }, [project, thresholdValues.lookbackDays]);

  // Refetch history and clear counts and instruction items when project changes
  useUpdateEffect(() => {
    if (project) {
      taskCounts.value = undefined;
      metricsData.value = undefined;
      fetchProjectInstrItems();
      fetchProjectBatches();
    }
  }, [project, thresholdValues.lookbackDays]);

  // Only load task counts once for a project, and display the setup modal when they finish loading
  useUpdateEffect(() => {
    if (!taskCounts.loading && !taskCounts.value) {
      fetchTaskCounts();
    }
    if (!metricsData.loading && !metricsData.value) {
      fetchMetricsData();
    }
  }, [taskCounts, metricsData, thresholdValues.lookbackDays]);

  // Error handling
  useUpdateEffect(() => {
    if (taskCounts.error) {
      SAlert.error(`Error fetching task counts: ${taskCounts.error.message}`);
    }
    if (metricsData.error) {
      SAlert.error(`Error fetching metrics: ${metricsData.error.message}`);
    }
    if (projectInstrItems.error) {
      SAlert.error(
        `Error fetching instruction items for project: ${projectInstrItems.error.message}`,
      );
    }
    if (projectBatches.error) {
      SAlert.error(`Error fetching instruction items for project: ${projectBatches.error.message}`);
    }
  }, [taskCounts.error, projectInstrItems.error, projectBatches.error]);

  useMount(() => {
    fetchTaskCounts();
    fetchMetricsData();
    fetchProjectBatches();
    fetchProjectInstrItems();
  });

  return (
    <>
      {metricsData.value && taskCounts.value && (
        <>
          <ThresholdFields
            speedAuditFilter={true}
            setThresholdValues={setThresholdValues}
            onRecalculateMetrics={_.noop}
            usedLookbackDays={thresholdValues.lookbackDays}
            metricsData={metricsData.value}
            taskCounts={taskCounts.value}
            projectBatches={projectBatches.value}
            instructionItems={projectInstrItems.value ?? []}
            reviewLevels={[reviewLevel]}
            setThresholdTasks={setThresholdTasks}
          />
        </>
      )}
    </>
  );
};

// eslint-disable-next-line complexity
const SpeedAudit = (props: SpeedAuditProps) => {
  const { isRemoAdmin, selectedProject, selectedProjectName } = useRemoAdminCtx();
  const {
    onGetOrCreateBatchOverride,
    createQAOperationDialogMode,
    hideAdvancedFilters,
    qaOperationName,
  } = props;

  const [withThresholds, setWithThresholds] = useState<boolean>(false);
  const [showThresholds, setShowThresholds] = useState<boolean>(false);

  const [thresholdValues, setThresholdValues] = useState<IThresholdValues>({
    instrItemThresholds: {},
    batches: [],
    minimumBenchmarksPerII: {},
    desiredQuality: '.95',
    lookbackDays: 14,
  });
  // If URL has closeOnComplete on initial page load, this window will automatically close when the speed audit is done
  const closeOnComplete = React.useMemo(() => window.location.href.includes('closeOnComplete'), []);
  const isRemotasks = React.useMemo(() => window.location.host.includes('remotasks'), []);

  const version = props.version ?? SpeedAuditVersion.v1;
  const defaultAuditBatchId: string | undefined =
    props.auditBatchId ?? (props.url?.query?.batchId as string | undefined) ?? undefined;

  const queryObjectIds =
    props.url?.query?.ids &&
    props.url?.query?.project &&
    parseObjectIds(props.url.query.ids as string);

  const auditType = props.url?.query?.audit_type
    ? (props.url?.query?.audit_type as AuditType)
    : AuditType.DEFAULT;
  const user = props.url?.query?.user;

  const flagAudit = !!props.url?.query?.flag_audit;
  const [isAuditing, setIsAuditing] = useState(false);

  const [index, setIndex] = React.useState<number>(0);
  const [rawObjectIds, setRawObjectIds] = useState(props.url?.query?.ids ?? '');
  const [objectIds, setObjectIds] = useState<string[]>(
    (queryObjectIds as string[] | undefined) ?? [],
  );
  const [attemptAudits, setAttemptAudits] = useState<SpeedAuditLookup[]>([]);
  const currentAttemptAudit = useMemo(
    () => attemptAudits[index] as SpeedAuditLookup | undefined,
    [index, attemptAudits],
  );
  const [subtaskAssignments, subtaskAssignmentsActions] = useMap<
    PartialRecord<string, SubtaskAssignment>
  >({});
  const [speedAuditResults, speedAuditResultsActions] = useMap<
    PartialRecord<string, SpeedAuditResult>
  >({});

  const [auditInput, setAuditInput] = useState<string>('custom');
  const [taskerToAudit, setTaskerToAudit] = useState<string>('');
  const [levelToAudit, setLevelToAudit] = useState<string>('');
  const [numTasksToAudit, setNumTasksToAudit] = useState<number>(0);

  const [project, setProject] = useState<ISelectedProject | undefined>(
    props.url?.query?.project || selectedProject
      ? {
          label: (props.url?.query?.name as string) || selectedProjectName || selectedProject || '',
          value: (props.url?.query?.project as string) || selectedProject || '',
          type: (props.url?.query?.type as string) || '',
        }
      : undefined,
  );

  const history = useHistory();
  const [projectInstrItems, setProjectInstrItems] = useState<IInstructionItemJson[]>();
  const [speedAuditName, setSpeedAuditName] = useState<string>(qaOperationName ?? '');
  const [auditBatch, setAuditBatch] = useState<AuditBatch>();
  const [recentAuditBatches, setRecentAuditBatches] = useState<AuditBatch[]>();
  const [recentBMAuditBatches, setRecentBMAuditBatches] = useState<AuditBatch[]>();
  const [loadingAttempts, setLoadingAttempts] = useState(false);
  const [spotterModalIsOpen, setSpotterModalIsOpen] = useState(false);
  const [spotterAttemptIds, setSpotterAttemptIds] = useState<string[]>([]);
  const [qaOperation, setQaOperation] = useState<SpeedAuditOperation>();
  const [assignmentParams, setAssignmentParams] = useState<QAAssignmentParams>();
  const [reviewLevelNumericValue, setReviewLevelNumericValue] = useState<number | null>(null);
  const [thresholdTasks, setThresholdTasks] = useState<string>('');
  const [nonAuditedCounting, setNonAuditedCounting] = useState<Record<string, number>>({});
  const [allIds, setAllIds] = useState<Record<string, string[]>>({});
  const [maxAuditAmount, setMaxAuditAmount] = useState<number>(0);
  const [auditAmount, setAuditAmount] = useState<number>(0);
  const [auditViewType, setAuditViewType] = useState<AuditViewType>(AuditViewType.STANDARD);
  const [reasonViewType, setReasonViewType] = useState<string>('');
  const [claimTasks, setClaimTasks] = useState<boolean>(false);
  const [claimedTasksInfo, setClaimedTasksInfo] = useState<any[]>([]);
  const [notInProjectTasks, setNotInProjectTasks] = useState<any[]>([]);
  const speedAuditNameRef = useRef<string>(qaOperationName ?? '');
  speedAuditNameRef.current = speedAuditName;
  const auditViewTypeRef = useRef<AuditViewType>(AuditViewType.STANDARD);
  auditViewTypeRef.current = auditViewType;

  const projectIsChatOrChatV2 = useMemo<boolean>(() => isProjectChatOrChat2(project), [project]);

  const alertMessage: string | undefined =
    projectIsChatOrChatV2 && auditViewType !== AuditViewType.CHAT_BULK_AUDIT
      ? `Speed Audit v1 and v2 are not compatible with the newest features of Chat Bulk Audits. This project is ${project?.type} please consider using CBA.`
      : !projectIsChatOrChatV2 && auditViewType === AuditViewType.CHAT_BULK_AUDIT
      ? `Chat Builk Audit supports only Chat Task v1 and Chat Task v2, this project is ${project?.type}, Please consider using another option.`
      : undefined;

  const alertSeverity =
    projectIsChatOrChatV2 && auditViewType !== AuditViewType.CHAT_BULK_AUDIT
      ? 'warning'
      : !projectIsChatOrChatV2 && auditViewType === AuditViewType.CHAT_BULK_AUDIT
      ? 'error'
      : undefined;

  const useTextBasedAudit = useUnleashFeatureFlag('rapid-text-based-audit', {}, false);

  const name = React.useMemo(
    () => (createQAOperationDialogMode ? qaOperationName : speedAuditName),
    [qaOperationName, speedAuditName, createQAOperationDialogMode],
  );

  useEffect(() => {
    validateQAOperationParams(qaOperation);
  }, [qaOperation]);

  const fetchNonAuditedCounting = useCallback(async () => {
    setNonAuditedCounting({});
    setAllIds({});
    if (!project) {
      return false;
    }

    const result = await apiFetch<
      QMNonAuditedListSubtasksResponse,
      QMNonAuditedListSubtasksRequest
    >(null, `corp-api/qm/projects/${project.value}/nonAudited`, {
      method: 'POST',
      body: {
        filters: {
          reviewLevel: reviewLevelOptions,
        },
      },
    });
    setAllIds(result.allIds);
    setNonAuditedCounting(result.reviewLevelCounts);
  }, [project]);

  useEffect(() => {
    if (!project) {
      setRawObjectIds([]);
      setObjectIds([]);
    }
    fetchNonAuditedCounting();
  }, [project, fetchNonAuditedCounting]);

  useEffect(() => {
    const numValue = parseInt(auditInput.replace(/\D+/g, ''));
    setReviewLevelNumericValue(isNaN(numValue) ? null : numValue);
    if (!hideAdvancedFilters) {
      setWithThresholds(!(isNaN(numValue) || numValue < 10));
    }
  }, [auditInput, hideAdvancedFilters]);

  const { loading: loadingReviewLevelSubtasks } = useAsync(async () => {
    const reviewNotNull = reviewLevelNumericValue !== null;
    const isHighReviewLevel =
      reviewNotNull && HIGH_REVIEW_LEVELS.indexOf(reviewLevelNumericValue) !== -1;
    if (!hideAdvancedFilters) setShowThresholds(isHighReviewLevel);
    if (reviewNotNull) await loadSubtasks(reviewLevelNumericValue);
  }, [reviewLevelNumericValue, setShowThresholds, hideAdvancedFilters]);

  const { loading: loadingSubtasksByTasker } = useAsync(async () => {
    const allFieldsValid = levelToAudit && taskerToAudit && numTasksToAudit;
    if (allFieldsValid && auditInput === 'tasker')
      await loadSubtasksByTasker(levelToAudit, taskerToAudit, numTasksToAudit);
  }, [auditInput, levelToAudit, taskerToAudit, numTasksToAudit]);

  useEffect(() => {
    if (!showThresholds) {
      setWithThresholds(false);
    }
  }, [setWithThresholds, showThresholds]);
  useEffect(() => setThresholdTasks(''), [withThresholds]);

  useEffect(() => {
    if (!hideAdvancedFilters) setObjectIds(parseObjectIds(thresholdTasks));
  }, [thresholdTasks, hideAdvancedFilters]);

  useEffect(() => {
    if (auditInput !== 'custom' && auditInput !== 'tasker') {
      const sanitizedOption = auditInput.replace(/\D+/g, '');
      const nonAudited = nonAuditedCounting[sanitizedOption] ?? QMNonAuditedListSubtasksLimit;
      setMaxAuditAmount(nonAudited);
    } else {
      setMaxAuditAmount(QMNonAuditedListSubtasksLimit);
    }
  }, [auditInput, nonAuditedCounting]);

  // autopopulate project if project not there yet
  useEffect(() => {
    const autopopulateProject = async () => {
      const ids: string[] = [...objectIds, project?.value].filter(Boolean) as string[];
      if (ids.length > 0 && !project?.type) {
        const result = await apiFetch(null, `corp-api/audit/projectFromObjectId`, {
          method: 'POST',
          body: {
            objectId: ids[0],
          },
        });
        if (result?.projectName && result?.projectId) {
          setProject({
            label: `${result.projectName} (${result.projectId})`,
            value: result.projectId,
            type: result.type,
          });
        }
      }
    };

    autopopulateProject().catch(e => {
      SAlert.error(`Failed to autopopulate project given object IDs `);
      logger.error(e);
    });
  }, [objectIds?.[0], project?.value]);

  const getNextUnauditedObject = useCallback(
    (attemptAudits: SpeedAuditLookup[], startIndex: number) => {
      for (let i = startIndex + 1; i < attemptAudits.length; i++) {
        const objectLookup = attemptAudits[i];

        const isNotAudited =
          version === SpeedAuditVersion.v1
            ? !objectLookup.result && !speedAuditResults[objectLookup.auditLookup.id]?.result
            : !objectLookup.resultV2?.length &&
              !speedAuditResults[objectLookup.auditLookup.id]?.resultV2?.length;

        if (isNotAudited) return { audit: objectLookup, index: i };
      }

      const lastIndex = attemptAudits.length - 1;
      return { audit: attemptAudits[lastIndex], index: lastIndex };
    },
    [speedAuditResults, version],
  );

  const getPrevUnauditedObject = useCallback(
    (attemptAudits: SpeedAuditLookup[], startIndex: number) => {
      for (let i = startIndex - 1; i > 0; i--) {
        const objectLookup = attemptAudits[i];
        if (!objectLookup.result && !speedAuditResults[objectLookup.auditLookup.id]?.result)
          return { audit: objectLookup, index: i };
      }

      return { audit: attemptAudits[0], index: 0 };
    },
    [speedAuditResults],
  );

  /** When setting the current index, we load this many Objects after that index */
  const bufferSize = 10;

  /** Keeps track of what Objects still need to be loaded and the order they will be loaded in */
  const loadPriorityQueue = useRef<SpeedAuditLookup[]>([]);

  const resetPriorityQueue = useCallback(() => {
    loadPriorityQueue.current = [];
  }, []);

  const initializePriorityQueue = useCallback(
    (objectLookups: SpeedAuditLookup[]) => {
      // Load objects that haven't been audited before those that have
      const sortedObjects = [...objectLookups].sort((a, b) => {
        if (a.result && !b.result) return 1;
        if (!a.result && b.result) return -1;
        return 0;
      });

      // Load the first object that hasn't been audited first
      // Also load x number of objects after it
      const { index: nextUnauditedIndex } = getNextUnauditedObject(sortedObjects, -1);
      const splicedObjectIds = sortedObjects.splice(nextUnauditedIndex, bufferSize);
      sortedObjects.unshift(...splicedObjectIds);
      loadPriorityQueue.current = sortedObjects;
    },
    [getNextUnauditedObject],
  );

  /**
   * Updates the Object Index and updates the loading queue to load the Object immediately (if needed)
   *
   * @param indexOrCallback - Either an index, or a callback that receives the previous index and returns a new index
   */
  const updateIndex = useCallback(
    (indexOrCallback: number | ((prev: number) => number)) => {
      setIndex(prev => {
        const newIndex =
          typeof indexOrCallback === 'function' ? indexOrCallback(prev) : indexOrCallback;
        const boundedIndex = Math.max(Math.min(newIndex, attemptAudits.length - 1), 0);
        if (newIndex === prev) return boundedIndex;

        const objectId = attemptAudits[boundedIndex].auditLookup.id;
        const priorityIndex = loadPriorityQueue.current.findIndex(
          audit => audit.auditLookup.id === objectId,
        );
        if (priorityIndex === -1) {
          return boundedIndex; // Already loaded
        }

        // Load x number of objects after the selected object
        const splicedObjectIds = loadPriorityQueue.current.splice(priorityIndex, bufferSize);
        loadPriorityQueue.current.unshift(...splicedObjectIds);
        return boundedIndex;
      });
    },
    [attemptAudits],
  );

  const nextIndex = useCallback(() => {
    updateIndex(prev => prev + 1);
  }, [updateIndex]);

  const prevIndex = useCallback(() => {
    updateIndex(prev => prev - 1);
  }, [updateIndex]);

  const nextUnauditedIndex = useCallback(() => {
    updateIndex(prev => getNextUnauditedObject(attemptAudits, prev).index);
  }, [attemptAudits, getNextUnauditedObject, updateIndex]);

  const prevUnauditedIndex = useCallback(() => {
    updateIndex(prev => getPrevUnauditedObject(attemptAudits, prev).index);
  }, [attemptAudits, getPrevUnauditedObject, updateIndex]);

  // loadObjectIds needs to know if the user is no longer auditing or is auditing a different batch
  // If either of these changes, it will cancel loading the current loadPriorityQueue
  const currentAuditBatchId = useRef('');
  const isAuditingRef = useRef(false);
  useEffect(() => {
    isAuditingRef.current = isAuditing;
  }, [isAuditing]);

  /**
   * Loads Object IDs sequentially, but asynchronously so that users can begin auditing some Objects
   * even before all Objects have been loaded
   */
  const loadObjectIds = useCallback(
    async (attemptAudits: SpeedAuditLookup[], objectIds: string[], auditBatchId: string) => {
      initializePriorityQueue(attemptAudits);
      currentAuditBatchId.current = auditBatchId;
      isAuditingRef.current = true;

      // Automatically skip to the last Object that hasn't been audited yet (for resuming an existing audit)
      const firstUnauditedIndex = getNextUnauditedObject(attemptAudits, -1).index;
      setIndex(firstUnauditedIndex);

      while (loadPriorityQueue.current.length) {
        // Cancel loading if the user exits the batch they were speed auditing
        if (auditBatchId !== currentAuditBatchId.current || !isAuditingRef.current) {
          resetPriorityQueue();
          return;
        }

        // Load a batch of audits at once to make use of network concurrency
        const batchSize = 4;
        const attemptAuditBatch = loadPriorityQueue.current.splice(0, batchSize);
        await Bluebird.map(attemptAuditBatch, async attemptAudit => {
          const objectId = attemptAudit.auditLookup.id;
          try {
            const rawTaskOrSubtaskAssignment = await getTaskOrSubtaskAssignment(objectId);
            const subtaskAssignment: SubtaskAssignment = {
              auditLookup: {} as any,
              responses: [],
              subtask: {} as any,
              ...rawTaskOrSubtaskAssignment,
              attemptAuditId: attemptAudit._id,
              result: attemptAudit.result,
              flagResult: attemptAudit.flagResult,
              comments: attemptAudit.comments,
              taskInstructionItems: attemptAudit.taskInstructionItems,
              perAnnotationInstructionItems: attemptAudit.perAnnotationInstructionItems,
            };

            subtaskAssignmentsActions.set(objectId, subtaskAssignment);
            speedAuditResultsActions.set(objectId, getInitialResult(subtaskAssignment));
          } catch (e) {
            SAlert.error(
              `Failed to load Attempt ${objectId} ${
                e.response.error ? `(${e?.response?.error})` : ''
              }`,
            );
            logger.error(e);
            if (e?.response?.customer) {
              // denied, probably because of RemoAdmin
              setIsAuditing(false);
            }
          }
        });
      }
    },
    [
      getNextUnauditedObject,
      initializePriorityQueue,
      resetPriorityQueue,
      speedAuditResultsActions,
      subtaskAssignmentsActions,
    ],
  );

  const fetchRecentBatches = useCallback(async () => {
    const { auditBatches, benchmarkAuditBatches } = await apiFetch(
      null,
      `corp-api/attemptAudit/auditBatches`,
      { method: 'GET' },
    );
    setRecentAuditBatches(auditBatches);
    setRecentBMAuditBatches(benchmarkAuditBatches);
  }, []);

  const fetchSpotterAttempts = useCallback(async () => {
    if (auditType === AuditType.SPOTTER_AUDIT && user) {
      setLoadingAttempts(true);
      const { attempts } = await apiFetch<ISpotterAttempts>(
        null,
        `corp-api/quality/spotterAudits/attempts?projectId=${props.url?.query?.project}&userId=${user}`,
        {
          method: 'GET',
        },
      );
      const attemptIds = attempts.map(attempt => attempt._id);
      setSpotterAttemptIds(attemptIds);
      setSpotterModalIsOpen(true);
      setLoadingAttempts(false);
    }
  }, [auditType, props.url, user]);

  useMount(() => {
    // Load an existing Batch (if any) from the URL Query Parameter
    if (defaultAuditBatchId) {
      handleLoadAuditBatch(defaultAuditBatchId, version).catch(e => {
        SAlert.error(`Failed to load Audit Batch ${defaultAuditBatchId}`);
        logger.error(e);
      });
    }

    fetchRecentBatches().catch(e => {
      SAlert.error(`Failed to load Recent Batches`);
      logger.error(e);
    });

    fetchSpotterAttempts().catch(e => {
      SAlert.error(`Failed to load Spotter Attempts`);
      logger.error(e);
    });
  });

  const resetAuditBatch = useCallback(() => {
    subtaskAssignmentsActions.reset();
    speedAuditResultsActions.reset();
    setIndex(0);
    setIsAuditing(false);
  }, [speedAuditResultsActions, subtaskAssignmentsActions]);

  const handleTextBasedAuditChecks = React.useCallback(async () => {
    resetAuditBatch();

    const ids: string[] = [];
    if (auditInput !== 'custom' && auditInput !== 'tasker') {
      const sanitizedOption: string = auditInput.replace(/\D+/g, '');
      const selectIds: string[] = allIds[sanitizedOption];
      ids.push(...selectIds.slice(0, auditAmount));
    } else {
      ids.push(..._.uniq(objectIds));
    }

    const [{ claimedTasksInfo }, { notInProjectTasks }] = await Promise.all([
      apiFetch(null, `corp-api/attemptAudit/checkClaims`, {
        method: 'POST',
        body: {
          ids,
        },
      } as any),
      apiFetch(null, `corp-api/attemptAudit/checkTaskProject`, {
        method: 'POST',
        body: {
          project: project?.value,
          ids,
        },
      } as any),
    ]);

    setClaimedTasksInfo(claimedTasksInfo);
    setNotInProjectTasks(notInProjectTasks);
    if (_.isEmpty(claimedTasksInfo) && _.isEmpty(notInProjectTasks)) {
      await handleCreateAuditBatch();
    }
  }, [allIds, auditAmount, auditInput, objectIds, resetAuditBatch, project]);

  const handleStandardAuditChecks = React.useCallback(async () => {
    resetAuditBatch();

    const ids: string[] = [];
    if (auditInput !== 'custom' && auditInput !== 'tasker') {
      const sanitizedOption: string = auditInput.replace(/\D+/g, '');
      const selectIds: string[] = allIds[sanitizedOption];
      ids.push(...selectIds.slice(0, auditAmount));
    } else {
      ids.push(..._.uniq(objectIds));
    }

    const { notInProjectTasks } = await apiFetch(null, `corp-api/attemptAudit/checkTaskProject`, {
      method: 'POST',
      body: {
        project: project?.value,
        ids,
      },
    } as any);

    setNotInProjectTasks(notInProjectTasks);
    if (_.isEmpty(notInProjectTasks)) {
      await handleCreateAuditBatch();
    }
  }, [allIds, auditAmount, auditInput, objectIds, resetAuditBatch, project]);

  const handleCreateAuditBatch = React.useCallback(
    async (idsToRemove?: string[]) => {
      resetAuditBatch();

      const ids: string[] = [];
      if (auditInput !== 'custom' && auditInput !== 'tasker') {
        const sanitizedOption: string = auditInput.replace(/\D+/g, '');
        const selectIds: string[] = allIds[sanitizedOption];
        ids.push(...selectIds.slice(0, auditAmount));
      } else if (idsToRemove) {
        ids.push(..._.uniq(_.difference(objectIds, idsToRemove)));
      } else {
        ids.push(..._.uniq(objectIds));
      }
      setClaimedTasksInfo([]);
      setNotInProjectTasks([]);

      try {
        const body: CreateAuditBatchBody = {
          projectId: project?.value,
          name: name || speedAuditNameRef.current,
          objectIds: ids as string[],
          type: auditType,
          assignmentParams,
          auditViewType: auditViewTypeRef.current,
          claimTasks,
        };

        if (reasonViewType) {
          logger.info(
            `Creating audit batch with non best Audit View Type, reason: ${reasonViewType}`,
          );
        }

        const { auditBatch, attemptAudits, projectInstrItems, qaOperation, erroredTasks } =
          await apiFetch<CreateAuditBatchResponse>(null, `corp-api/attemptAudit/auditBatches`, {
            method: 'POST',
            body,
          } as any);

        if (!_.isEmpty(erroredTasks)) {
          alert(
            `The following tasks were not able to be added to the audit. This might be because the task wasn't found, it doesn't have any subtasks or attempts, or the last response is blank and there is nothing to audit.
          List of errored tasks: ${erroredTasks.join(', ')}`,
          );
        }

        logCorpEvent(Event.SpeedAuditCreated, {
          audit_name: name ?? '',
          audit_id: auditBatch._id as string,
          audit_project: project?.value ?? '',
          speed_audit_version: version,
        } as SpeedAuditCreated).catch(SAlert.error);

        // Instead of starting Speed Audit, redirect user to Operations Dashboard
        if (onGetOrCreateBatchOverride) {
          onGetOrCreateBatchOverride(auditBatch._id);
          return;
        } else if (qaOperation && project?.value) {
          redirectToOperationsPage(isRemoAdmin, project.value);
          return;
        }

        setProjectInstrItems(projectInstrItems as IInstructionItemJson[]);
        setIsAuditing(true);
        setAuditBatch(auditBatch as any);
        setAttemptAudits(attemptAudits as any);
        setQaOperation(qaOperation);

        loadObjectIds(attemptAudits as any, ids, auditBatch._id).catch(SAlert.error);
        // Only redirect if not on remotasks.com (/v2/speed_audit/ doesn't exist on remotasks.com)
        if (!isRemotasks) {
          history &&
            history.push(
              auditBatch.auditViewType === AuditViewType.CHAT_BULK_AUDIT
                ? '/chat_bulk_audit/' + auditBatch._id
                : version === SpeedAuditVersion.v1
                ? '/speed_audit/' + auditBatch._id
                : '/v2/speed_audit/' + auditBatch._id,
            );
        }
      } catch (e) {
        defaultApiFetchErrorHandler(e, { handleHttp: true });
        return;
      }
    },
    [
      objectIds,
      claimTasks,
      auditInput,
      allIds,
      auditAmount,
      auditType,
      name,
      project,
      assignmentParams,
      isRemoAdmin,
      version,
      resetAuditBatch,
      history,
      loadObjectIds,
      onGetOrCreateBatchOverride,
      reasonViewType,
      location,
    ],
  );

  const onChangeRawObjectIds = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setRawObjectIds(event.target.value);
    setObjectIds(parseObjectIds(event.target.value));
  };

  // auditBatchId is required, but the types in react-use are incorrect (fixed in v14.0)
  const [loadAuditBatch, handleLoadAuditBatch] = useAsyncFn(
    async (auditBatchId?: string, version?: SpeedAuditVersion) => {
      if (onGetOrCreateBatchOverride && auditBatchId) {
        onGetOrCreateBatchOverride(auditBatchId);
        return;
      }
      resetAuditBatch();

      try {
        const { attemptIds, auditBatch, projectId, projectInstrItems, qaOperation } =
          await apiFetch<GetAuditBatchResponse>(
            null,
            `corp-api/attemptAudit/auditBatches/${auditBatchId}`,
            {
              method: 'GET',
            },
          );
        const { attemptAudits } = auditBatch;
        setAuditBatch(auditBatch as any);
        setIsAuditing(true);
        setProjectInstrItems(projectInstrItems);
        setProject({ label: '', value: projectId, type: '' });
        setObjectIds(attemptIds);
        setAttemptAudits(attemptAudits as any);
        setQaOperation(qaOperation);

        loadObjectIds(attemptAudits as any, attemptIds, auditBatch._id).catch(SAlert.error);
        // Only redirect if not on remotasks.com (/v2/speed_audit/ doesn't exist on remotasks.com)
        if (!isRemotasks) {
          history &&
            history.push(
              auditBatch.auditViewType === AuditViewType.CHAT_BULK_AUDIT
                ? '/chat_bulk_audit/' + auditBatch._id
                : version === SpeedAuditVersion.v1
                ? '/speed_audit/' + auditBatch._id
                : '/v2/speed_audit/' + auditBatch._id,
            );
        }
      } catch (e) {
        defaultApiFetchErrorHandler(e, { handleHttp: true });
      }
    },
    [resetAuditBatch, history, loadObjectIds, onGetOrCreateBatchOverride],
  );

  useEffect(() => {
    if (props.forceRedirectAuditId) {
      handleLoadAuditBatch(props.forceRedirectAuditId, version);
    }
  }, [props.forceRedirectAuditId, handleLoadAuditBatch, version]);

  const [updateAuditBatch, handleUpdateAuditBatch] = useAsyncFn(
    async (auditBatch?: any, attemptIds: string[] = []) => {
      resetAuditBatch();

      if (!auditBatch) return;
      const { attemptAudits } = auditBatch;
      setAttemptAudits(attemptAudits);
      setIsAuditing(true);

      loadObjectIds(attemptAudits, attemptIds, auditBatch._id).catch(SAlert.error);
    },
  );

  const handleSpotterAuditModalAction = useCallback(
    (attemptIds: string[]) => {
      setObjectIds(attemptIds);
      setSpotterModalIsOpen(false);
    },
    [setObjectIds, setSpotterModalIsOpen],
  );

  const loadSubtasks = useCallback(
    async (reviewLevel: ReviewLevel) => {
      if (!project?.value) {
        SAlert.error(`Cannot load L${reviewLevel} subtasks since no project ID was given.`);
        return;
      }
      const value = await apiFetch(null, `corp-api/lookup/${project?.value}/subtasks`, {
        method: 'GET',
        query: {
          reviewLevel,
        },
      });

      setRawObjectIds(value);
      setObjectIds(parseObjectIds(value));
    },
    [project],
  );

  const loadSubtasksByTasker = useCallback(
    async (reviewLevel: string, taskerEmail: string, numTasks: number) => {
      if (!project?.value) {
        SAlert.error(`Cannot load L${reviewLevel} subtasks since no project ID was given.`);
        return;
      }
      const value = await apiFetch(null, `corp-api/attemptAudit/subtaskIdsByTasker`, {
        query: {
          reviewLevel: parseInt(reviewLevel.replace(/\D+/g, '')),
          taskerEmail,
          projectId: project?.value,
          numTasks,
        },
      });

      setRawObjectIds(value);
      setObjectIds(parseObjectIds(value));
    },
    [project],
  );

  const handleComplete = useCallback(() => {
    setIsAuditing(false);
    if (closeOnComplete) {
      window.close();
    } else {
      history?.push(version === SpeedAuditVersion.v1 ? '/speed_audit' : '/v2/speed_audit');
    }
  }, [closeOnComplete, history, version]);

  const submitButtonLabel = React.useMemo<string>(() => {
    const validAmount =
      auditInput === 'custom' || auditInput === 'tasker'
        ? true
        : auditAmount > 0 && auditAmount <= maxAuditAmount;

    const projectIsChatOrChatV2 = isProjectChatOrChat2(project);

    const notBestAuditTypeWasSelected: boolean =
      (projectIsChatOrChatV2 && auditViewType !== AuditViewType.CHAT_BULK_AUDIT) ||
      (!projectIsChatOrChatV2 && auditViewType === AuditViewType.CHAT_BULK_AUDIT);
    const missingReason = notBestAuditTypeWasSelected && !reasonViewType;

    if (!project?.value) return 'Select a project first';
    else if (_.isEmpty(name)) return 'Please enter audit batch name';
    else if (withThresholds && !thresholdTasks) return 'No tasks for the specified filters';
    else if (createQAOperationDialogMode || assignmentParams) return 'Create new Audit Batch';
    else if (!validAmount) return 'Please enter a valid # amount to audit';
    else if (missingReason) return 'Please add a reason for selecting that audit view type';
    return auditInput === 'custom'
      ? `Start Custom Audit`
      : auditInput === 'tasker'
      ? `Start ${levelToAudit} audit for ${taskerToAudit}`
      : `Start L${reviewLevelNumericValue} Audit`;
  }, [
    project,
    name,
    auditInput,
    withThresholds,
    thresholdTasks,
    auditAmount,
    maxAuditAmount,
    reviewLevelNumericValue,
    createQAOperationDialogMode,
    assignmentParams,
    levelToAudit,
    taskerToAudit,
    project,
    reasonViewType,
  ]);

  const canSubmitNewBatch = React.useMemo<boolean>(() => {
    const isLoading =
      loadingReviewLevelSubtasks ||
      loadAuditBatch.loading ||
      loadingAttempts ||
      loadingSubtasksByTasker;
    const validAmount =
      auditInput === 'custom' || auditInput === 'tasker'
        ? true
        : auditAmount > 0 && auditAmount <= maxAuditAmount;

    const textBasedLoading =
      auditInput === 'tasker' && (!taskerToAudit || !levelToAudit || !numTasksToAudit);

    const projectIsChatOrChatV2 = isProjectChatOrChat2(project);

    const notBestAuditTypeWasSelected: boolean =
      (projectIsChatOrChatV2 && auditViewType !== AuditViewType.CHAT_BULK_AUDIT) ||
      (!projectIsChatOrChatV2 && auditViewType === AuditViewType.CHAT_BULK_AUDIT);
    const missingReason = notBestAuditTypeWasSelected && !reasonViewType;

    const disableSubmit: boolean =
      isLoading ||
      objectIds.length === 0 ||
      _.isEmpty(name) ||
      !validAmount ||
      textBasedLoading ||
      missingReason;

    return !disableSubmit;
  }, [
    loadingReviewLevelSubtasks,
    loadAuditBatch.loading,
    loadingAttempts,
    loadingSubtasksByTasker,
    auditInput,
    auditAmount,
    maxAuditAmount,
    taskerToAudit,
    levelToAudit,
    numTasksToAudit,
    objectIds,
    name,
    project,
    reasonViewType,
    auditViewType,
  ]);

  if (updateAuditBatch.loading) {
    return (
      <Typography variant="h6">
        Fetching new audits with updated thresholds/manually excluded reviewers...
        <Spinner center size="md" />
      </Typography>
    );
  }

  if (loadAuditBatch.loading || loadingAttempts) {
    return <Spinner center size="md" />;
  }

  let drAuditLink = window.location.href;
  if (!props.url?.query?.drAudit) {
    drAuditLink += (drAuditLink.includes('?') ? '&' : '?') + 'drAudit=1';
  }

  return (
    <div>
      <SpotterAuditModal
        isOpen={spotterModalIsOpen && !!spotterAttemptIds.length}
        attemptIds={spotterAttemptIds}
        setAuditAttemptIds={handleSpotterAuditModalAction}
        onRequestClose={() => setSpotterModalIsOpen(false)}
      />
      {!props.isPodHealth && (
        <Grid container gap={2} direction={'row'} zeroMinWidth>
          <Grid item xs={6}>
            <Stack spacing={1}>
              <Typography variant="h5">
                Speed Audit{' '}
                {version === SpeedAuditVersion.v2 && (
                  <span style={{ marginLeft: '10px' }}>
                    <Chip label={'BETA'} color={'primary'} />
                  </span>
                )}
              </Typography>
              {version === SpeedAuditVersion.v1 && !props.hideVersionChoice && (
                <Alert severity="warning">
                  This is an old version of speed audit!
                  <IsomorphicLink href={'/v2/speed_audit'}>
                    {' '}
                    Try out the new version
                  </IsomorphicLink>{' '}
                  (switch back any time)
                </Alert>
              )}
              <Typography variant="body2">
                Use this tool to quickly look at a bunch of data and find out what's happening on
                your queue!
              </Typography>
              <Typography variant="body2">
                {!props.url?.query?.drAudit && (
                  <>
                    <a href={drAuditLink}>Click here</a> to audit GIS Drivable Regions
                  </>
                )}
              </Typography>
              <Typography variant="subtitle2">
                1. Name your audit batch
                {createQAOperationDialogMode ? ' (copied from operation name)' : ''}
              </Typography>
              <TextInput
                value={name}
                disabled={createQAOperationDialogMode}
                onChange={e => setSpeedAuditName(e.target.value)}
                placeholder={
                  createQAOperationDialogMode
                    ? 'Enter operation name above'
                    : 'Speed Audit Batch Name'
                }
                size="large"
                label={
                  createQAOperationDialogMode && !name
                    ? 'Enter operation name above'
                    : 'Audit Batch Name'
                }
                helperText="*required"
              />
              {props.showProjectChooser && (
                <>
                  <Typography variant="subtitle2">2. Select a project </Typography>
                  <SelectProject
                    noRemoAdminCtxUpdate={createQAOperationDialogMode}
                    onChange={setProject}
                    value={project}
                    width="100%"
                  />
                </>
              )}
              {useTextBasedAudit && (
                <>
                  <Typography variant="subtitle2">3. What type of audit is this?</Typography>
                  {alertMessage && project ? (
                    <div>
                      <Text color={alertSeverity}>{alertMessage}</Text>
                      <TextArea
                        value={reasonViewType}
                        onChange={event => {
                          setReasonViewType(event.target.value);
                        }}
                        placeholder="Please add a reason why you are not selecting the recommended option"
                      />
                    </div>
                  ) : null}
                  <RadioGroup
                    value={auditViewType}
                    onChange={value => {
                      setAuditViewType(value as AuditViewType);
                      setAuditInput('custom');
                    }}
                  >
                    <Radio
                      key="standard-audit"
                      value={AuditViewType.STANDARD}
                      label="Standard Audit View"
                      labelPlacement="end"
                    />
                    <Radio
                      key="text-based"
                      value={AuditViewType.TEXT_BASED}
                      label="Text Based Audit View"
                      labelPlacement="end"
                    />
                    <Radio
                      key="chat-bulk-audit"
                      value={AuditViewType.CHAT_BULK_AUDIT}
                      label="Chat Bulk Audit View (For Chat Tasks Only)"
                      labelPlacement="end"
                    />
                  </RadioGroup>
                </>
              )}
              {auditViewType === AuditViewType.STANDARD && (
                <>
                  <Typography variant="subtitle2">
                    {useTextBasedAudit ? '4' : '3'}. What do you want to audit?
                  </Typography>
                  <RadioGroup value={auditInput} onChange={setAuditInput}>
                    {reviewLevelOptions.map((rl: ReviewLevel) => {
                      const optionValue = `l${rl}`;
                      const pendingAmount =
                        nonAuditedCounting[rl] === QMNonAuditedListSubtasksLimit
                          ? `over ${QMNonAuditedListSubtasksLimit}`
                          : nonAuditedCounting[rl] ?? 'loading';
                      let label: string = `L${rl} Tasks `;
                      label += project?.value
                        ? `(non audited: ${pendingAmount})`
                        : '(please select a project)';
                      return (
                        <Radio
                          key={optionValue}
                          value={optionValue}
                          disabled={!project?.value || loadingReviewLevelSubtasks}
                          label={label}
                          labelPlacement="end"
                        />
                      );
                    })}
                    <Radio
                      key="custom"
                      value="custom"
                      label="I have my own IDs"
                      labelPlacement="end"
                    />
                  </RadioGroup>
                </>
              )}
              {auditViewType === AuditViewType.TEXT_BASED && (
                <>
                  <Typography variant="subtitle2">
                    {useTextBasedAudit ? '4' : '3'}. What do you want to audit?
                  </Typography>
                  <RadioGroup value={auditInput} onChange={setAuditInput}>
                    <Radio
                      key="tasker"
                      value="tasker"
                      label="Audit by tasker and level"
                      labelPlacement="end"
                    />
                    <Radio
                      key="custom"
                      value="custom"
                      label="I have my own IDs"
                      labelPlacement="end"
                    />
                  </RadioGroup>
                </>
              )}
              {auditInput === 'custom' ? (
                <TextInput
                  value={rawObjectIds}
                  onChange={onChangeRawObjectIds}
                  fullWidth={true}
                  multiline={true}
                  placeholder="Object IDs"
                  size="large"
                  label="Object IDs"
                  helperText={
                    auditViewType === AuditViewType.CHAT_BULK_AUDIT
                      ? 'Tasks or Task Attempts'
                      : 'Tasks, Subtasks, Task Attempts, Task Reviews, Training Attempts'
                  }
                />
              ) : auditInput === 'tasker' ? (
                <>
                  <Typography variant="subtitle2">
                    5. Select audit details (current level, last attempter/reviewer, number of
                    tasks)
                  </Typography>
                  <RadioGroup value={levelToAudit} onChange={setLevelToAudit}>
                    {reviewLevelOptions.map((rl: ReviewLevel) => {
                      const optionValue = `l${rl}`;
                      const pendingAmount =
                        nonAuditedCounting[rl] === QMNonAuditedListSubtasksLimit
                          ? `over ${QMNonAuditedListSubtasksLimit}`
                          : nonAuditedCounting[rl] ?? 'loading';
                      let label: string = `L${rl} Tasks `;
                      label += project?.value
                        ? `(non audited: ${pendingAmount})`
                        : '(please select a project)';
                      return (
                        <Radio
                          key={optionValue}
                          value={optionValue}
                          disabled={!project?.value || loadingSubtasksByTasker}
                          label={label}
                          labelPlacement="end"
                        />
                      );
                    })}
                  </RadioGroup>
                  <TextInput
                    value={taskerToAudit}
                    onChange={e => setTaskerToAudit(e.target.value)}
                    fullWidth={true}
                    multiline={true}
                    placeholder="email"
                    size="large"
                    label="Tasker Email"
                    helperText="Enter the email of the tasker you want to audit"
                  />
                  <TextInput
                    value={numTasksToAudit}
                    onChange={e => setNumTasksToAudit(parseInt(e.target.value))}
                    type="number"
                    fullWidth={true}
                    size="large"
                    label="Number of tasks to audit"
                    helperText="Enter the number of tasks you'd like to audit"
                  />
                </>
              ) : (
                <div>
                  <NumericInput value={auditAmount} onChange={setAuditAmount} size="sm" />
                  <h5>
                    This number must be lower or equals than the non audited amount {maxAuditAmount}
                  </h5>
                </div>
              )}
              {auditViewType === AuditViewType.TEXT_BASED && (
                <div>
                  <Typography variant="subtitle2">
                    Would you like to claim the tasks in this audit?
                  </Typography>
                  <Typography variant="body2">
                    This will place an indefinite claim on them, so they won't be worked on unless
                    you unclaim them or complete the audit.
                  </Typography>
                  <Checkbox
                    style={{
                      backgroundColor: '#f1f1f1',
                      borderRadius: '1em',
                      marginBottom: '1ex',
                      paddingRight: '1ex',
                    }}
                    label={'Claim tasks?'}
                    onChange={() => setClaimTasks(!claimTasks)}
                    checked={claimTasks}
                  />
                </div>
              )}
              {!hideAdvancedFilters &&
                project &&
                showThresholds &&
                reviewLevelNumericValue !== null && (
                  <div
                    style={{
                      borderTop: '1px solid black',
                      borderBottom: '1px solid black',
                      padding: 4,
                    }}
                  >
                    <h4>{useTextBasedAudit ? '5' : '4'} Set Advanced Thresholds (optional)</h4>
                    <Checkbox
                      style={{
                        backgroundColor: '#f1f1f1',
                        borderRadius: '1em',
                        marginBottom: '1ex',
                        paddingRight: '1ex',
                      }}
                      label={'Use advanced thresholds?'}
                      onChange={() => setWithThresholds(!withThresholds)}
                      checked={withThresholds}
                    />
                    {withThresholds && (
                      <SpeedAuditThresholdFields
                        project={project}
                        thresholdValues={thresholdValues}
                        setThresholdValues={setThresholdValues}
                        setThresholdTasks={setThresholdTasks}
                        reviewLevel={reviewLevelNumericValue}
                      />
                    )}
                  </div>
                )}
              <h4 style={{ border: '1px dotted lightgray', borderRadius: '1ex', padding: '1ex' }}>
                {objectIds.length} tasks for this audit.
              </h4>
              {!createQAOperationDialogMode && (
                <QAOperationAssignmentForm onChange={setAssignmentParams} />
              )}
              <Button
                disabled={!canSubmitNewBatch}
                onClick={async () =>
                  auditViewType === AuditViewType.TEXT_BASED
                    ? await handleTextBasedAuditChecks()
                    : await handleStandardAuditChecks().catch(SAlert.error)
                }
              >
                {submitButtonLabel}
              </Button>
            </Stack>
          </Grid>
          <Grid item style={{ maxWidth: '45%' }}>
            <Stack spacing={1}>
              <Typography variant="h5">Audit History</Typography>
              <div>
                <H3>Recent Audit Batches</H3>
                <RecentAuditBatchButtons
                  setVersion={props.setVersion}
                  recentAuditBatches={recentAuditBatches || []}
                  onLoadAuditBatch={handleLoadAuditBatch}
                  hideVersionChoice={props.hideVersionChoice}
                  setForceRedirectAuditId={props.setForceRedirectAuditId}
                />
              </div>
              <div>
                <H3>Recent Benchmark Threshold Audits</H3>
                <RecentAuditBatchButtons
                  setVersion={props.setVersion}
                  recentAuditBatches={recentBMAuditBatches || []}
                  onLoadAuditBatch={handleLoadAuditBatch}
                  hideVersionChoice={props.hideVersionChoice}
                  setForceRedirectAuditId={props.setForceRedirectAuditId}
                />
              </div>
            </Stack>
          </Grid>
        </Grid>
      )}
      {(!_.isEmpty(claimedTasksInfo) || !_.isEmpty(notInProjectTasks)) && (
        <WarningTasksDialog
          claimedTasksInfo={claimedTasksInfo}
          setClaimedTasksInfo={setClaimedTasksInfo}
          notInProjectTasks={notInProjectTasks}
          setNotInProjectTasks={setNotInProjectTasks}
          handleCreateAuditBatch={handleCreateAuditBatch}
        />
      )}

      {isAuditing && projectInstrItems && project && auditBatch && (
        <QAOperationContextProvider initialQAOperation={qaOperation} attemptResult={attemptAudits}>
          {auditBatch.auditViewType === AuditViewType.TEXT_BASED && useTextBasedAudit ? (
            <TextBasedSpeedAuditDialog
              auditBatch={auditBatch}
              projectId={project.value}
              objectIds={objectIds}
            />
          ) : (
            <SpeedAuditDialog
              projectInstrItems={projectInstrItems}
              projectId={project.value}
              objectIds={objectIds}
              auditBatch={auditBatch}
              onRequestClose={handleComplete}
              onUpdateAuditBatch={handleUpdateAuditBatch}
              flagAudit={flagAudit}
              subtaskAssignments={subtaskAssignments}
              speedAuditResults={speedAuditResults}
              speedAuditResultsActions={speedAuditResultsActions}
              currentAttemptAudit={currentAttemptAudit}
              index={index}
              setIndex={updateIndex}
              nextIndex={nextIndex}
              prevIndex={prevIndex}
              nextUnauditedIndex={nextUnauditedIndex}
              prevUnauditedIndex={prevUnauditedIndex}
              speedAuditVersion={version}
              user={props.user}
            />
          )}
        </QAOperationContextProvider>
      )}
    </div>
  );
};

const RecentAuditBatchButtons = (props: {
  recentAuditBatches?: AuditBatch[];
  onLoadAuditBatch: (auditBatchId?: string) => Promise<void>;
  hideVersionChoice: boolean | undefined;
  setVersion?: (newVersion: any) => void;
  setForceRedirectAuditId?: (redirectThisAuditId: string) => void;
}) => {
  const { recentAuditBatches, setVersion, setForceRedirectAuditId } = props;

  const [selectedMenu, setSelectedMenu] = React.useState<null | HTMLElement>(null);
  const [selectedAudit, setSelectedAudit] = React.useState<string>('');
  const [selectedAuditViewType, setSelectedAuditViewType] = React.useState<AuditViewType | null>();
  const open = Boolean(selectedMenu);
  const useTextBasedAudit = useUnleashFeatureFlag('rapid-text-based-audit', {}, false);

  const openMenu = (
    event: React.MouseEvent<HTMLElement>,
    auditId: string,
    auditViewType?: AuditViewType,
  ) => {
    setSelectedMenu(event.currentTarget);
    setSelectedAudit(auditId);
    setSelectedAuditViewType(auditViewType);
  };
  const closeMenu = () => {
    setSelectedMenu(null);
    setSelectedAudit('');
    setSelectedAuditViewType(null);
  };

  if (!recentAuditBatches || recentAuditBatches.length === 0) {
    return <div>No recent audit batches found</div>;
  }
  return (
    <Stack spacing={0}>
      {recentAuditBatches.map(auditBatch => {
        return (
          <div key={auditBatch._id}>
            <TextButton
              style={{ textAlign: 'left', maxWidth: '100%' }}
              type="primary"
              key={auditBatch._id}
              onClick={
                props.hideVersionChoice
                  ? () => props.onLoadAuditBatch(auditBatch._id)
                  : event => openMenu(event, auditBatch._id, auditBatch.auditViewType)
              }
              aria-controls={open ? 'basic-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
            >
              <span
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {auditBatch.name ?? auditBatch._id}
              </span>
            </TextButton>
          </div>
        );
      })}

      {setVersion && setForceRedirectAuditId && (
        <Menu
          MenuListProps={{
            'aria-labelledby': 'lock-button',
          }}
          anchorEl={selectedMenu}
          open={open && !props.hideVersionChoice}
          onClose={closeMenu}
        >
          {(!useTextBasedAudit || selectedAuditViewType !== AuditViewType.TEXT_BASED) && (
            <MenuItem
              onClick={() => {
                setVersion(SpeedAuditVersion.v2);
                setForceRedirectAuditId(selectedAudit);
              }}
            >
              Speed Audit v2
            </MenuItem>
          )}
          {useTextBasedAudit && selectedAuditViewType === AuditViewType.TEXT_BASED && (
            <MenuItem
              onClick={() => {
                setVersion(SpeedAuditVersion.v1);
                setForceRedirectAuditId(selectedAudit);
              }}
            >
              Text Based Audit
            </MenuItem>
          )}
        </Menu>
      )}
    </Stack>
  );
};

async function getTaskOrSubtaskAssignment(id: string) {
  return (await apiFetch(null, `corp-api/attemptAudit/lookup/${id}`, { method: 'GET' })) || {};
}

export default SpeedAudit;
